/**
 * Reports – Outsource Report.
 * GET /histopathology/reports/outsource/?from_date&to_date[&export=pdf]
 * Response totals: total_report, completed_report, delivered_report.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Stack, Paper, Typography, TextField, Tooltip, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { DateTime } from "luxon";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";
import CommonValidation from "../../Utility/CommonValidation";
import { CommonGridHeader } from "../../Components/Common Components/CommonComponents";
import CommonDatePicker from "../../Components/Common Components/CommonDatePicker";
import { CurrencySymbol } from "../../Utility/Constants";
import "../../Styles/Configuration.css";

const today = () => DateTime.now().toFormat("yyyy-MM-dd");

const SummaryCard = ({ label, value }) => (
  <Paper elevation={1} sx={{ px: 1.2, py: 0.9, minWidth: "7vw", borderRadius: "8px" }}>
    <Typography sx={{ fontSize: "0.95vw", fontWeight: 700, color: "#1a1a1a", lineHeight: 1.1 }}>{value}</Typography>
    <Typography sx={{ fontSize: "0.7vw", color: "#666" }}>{label}</Typography>
  </Paper>
);

export default function OutsourceReport() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [dateIndex, setDateIndex] = useState(1);
  const [search, setSearch] = useState("");
  const [totals, setTotals] = useState({ total: 0, transferred: 0, received: 0, uploaded: 0, pending: 0 });
  const [msg, setMsg] = useState({ err: false, errText: "", ok: false, okText: "" });

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (fromDate) params.append("from_date", fromDate);
    if (toDate) params.append("to_date", toDate);
    return params.toString();
  }, [fromDate, toDate]);

  const fetchData = useCallback(() => {
    setLoading(true);
    const url = `${Serviceurls.HISTOPATHOLOGY_REPORTS_OUTSOURCE}?${buildQuery()}`;
    RestAPIService.getAll(url)
      .then((res) => {
        if (res?.data?.status === "success") {
          setRows(res.data.data ?? []);
          const t = res.data.totals || {};
          setTotals({
            total: +t.total_count || 0,
            transferred: +t.transferred_count || 0,
            received: +t.received_count || 0,
            uploaded: +t.result_uploaded_count || 0,
            pending: +t.pending_count || 0,
          });
        } else {
          setRows([]);
          setTotals({ total: 0, transferred: 0, received: 0, uploaded: 0, pending: 0 });
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setRows([]);
        setTotals({ total: 0, transferred: 0, received: 0, uploaded: 0, pending: 0 });
        setMsg({ err: true, errText: err?.response?.data?.message || err?.message, ok: false, okText: "" });
      });
  }, [buildQuery]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      [r.patient_name, r.nxt_id, r.bill_number, r.sample_id, r.overall_sample_id, r.outsource_lab_name, r.test_details, r.outsource_status, r.result_status]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
    );
  }, [rows, search]);

  const handleExportPdf = () => {
    const url = `${Serviceurls.HISTOPATHOLOGY_REPORTS_OUTSOURCE}?${buildQuery()}&export=pdf`;
    RestAPIService.PrintPDF(url)
      .then((res) => {
        const file = new Blob([res.data], { type: "application/pdf" });
        window.open(URL.createObjectURL(file));
      })
      .catch(() => setMsg({ err: true, errText: "Unable to export PDF.", ok: false, okText: "" }));
  };

  const statusColor = (status) => {
    const s = String(status || "").toLowerCase();
    if (s.includes("uploaded") || s === "approved") return "#2e7d32";
    if (s.includes("received")) return "#0288d1";
    if (s.includes("transferred") || s === "saved" || s === "sent_for_approval") return "#ef6c00";
    return "#999";
  };

  const columns = [
    { field: "sno", headerName: "S No", flex: 0.4, minWidth: 60, sortable: false },
    {
      field: "patient_details", headerName: "Patient Details", flex: 1.4, minWidth: 200, sortable: false,
      renderCell: (p) => (
        <Box sx={{ lineHeight: 1.3 }}>
          <Typography variant="body2" fontWeight={600}>{p.row.patient_name || "-"}</Typography>
          <Typography variant="caption" color="text.secondary">
            {[p.row.nxt_id, p.row.patient_age, p.row.patient_gender].filter(Boolean).join(" | ") || "-"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "bill_details", headerName: "Bill Details", flex: 1.1, minWidth: 150, sortable: false,
      renderCell: (p) => (
        <Box sx={{ lineHeight: 1.3 }}>
          <Typography variant="body2">{p.row.bill_number || "-"}</Typography>
          <Typography variant="caption" color="text.secondary">{p.row.invoice_date || "-"}</Typography>
        </Box>
      ),
    },
    {
      field: "sample_id", headerName: "SID Details", flex: 1, minWidth: 130, sortable: false,
      renderCell: (p) => (
        <Box sx={{ lineHeight: 1.3 }}>
          <Typography variant="body2">{p.row.sample_id || "-"}</Typography>
          {p.row.overall_sample_id && (
            <Typography variant="caption" color="text.secondary">{p.row.overall_sample_id}</Typography>
          )}
        </Box>
      ),
    },
    { field: "outsource_lab_name", headerName: "Outsource Lab", flex: 1.1, minWidth: 140 },
    { field: "test_details", headerName: "Test Name", flex: 1.2, minWidth: 150 },
    {
      field: "outsource_status", headerName: "Status", flex: 1, minWidth: 130,
      renderCell: (p) => (
        <Box sx={{ lineHeight: 1.3 }}>
          <Typography sx={{ fontSize: "0.85vw", color: statusColor(p.row.outsource_status), fontWeight: 600 }}>
            {p.row.outsource_status || "-"}
          </Typography>
          {p.row.result_status && (
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "capitalize" }}>
              {String(p.row.result_status).replace(/_/g, " ")}
            </Typography>
          )}
        </Box>
      ),
    },
    { field: "due_date", headerName: "Due Date", flex: 0.8, minWidth: 110 },
  ];

  return (
    <Box sx={{ p: "0.8vw", height: "100%", display: "flex", flexDirection: "column", gap: "0.8vw", overflow: "hidden", minHeight: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <SummaryCard label="Total" value={totals.total} />
        <SummaryCard label="Transferred" value={totals.transferred} />
        <SummaryCard label="Received" value={totals.received} />
        <SummaryCard label="Result Uploaded" value={totals.uploaded} />
        <SummaryCard label="Pending" value={totals.pending} />
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ minWidth: "10vw" }}>
          <CommonDatePicker
            FromDate={fromDate} ToDate={toDate} DateIndex={dateIndex}
            HideAllMenu removeMarginRight
            SelectedDates={(from, to, idx) => { setFromDate(from); setToDate(to); setDateIndex(idx); }}
          />
        </Box>
        <TextField size="small" placeholder="Search" value={search}
          onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: "11vw" }} />
        <Tooltip title="Export PDF">
          <Button variant="outlined" size="small" startIcon={<FileDownloadOutlinedIcon />}
            onClick={handleExportPdf} sx={{ textTransform: "capitalize" }}>
            PDF
          </Button>
        </Tooltip>
      </Stack>
      <Paper sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex" }}>
        <DataGrid
          rows={filtered.map((r, i) => ({ ...r, id: r.id ?? `row-${i}`, sno: i + 1 }))}
          columns={columns}
          loading={loading}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(m) => { setPage(m.page); setPageSize(m.pageSize); }}
          pageSizeOptions={[10, 20, 30]}
          disableRowSelectionOnClick
          rowHeight={56}
          columnHeaderHeight={42}
          slots={{
            toolbar: CommonGridHeader.CommonToolBar,
            noRowsOverlay: () => <Stack className="eMed_conf_nodata">No Records Found</Stack>,
          }}
          sx={{ border: 0, flex: 1, minWidth: 0 }}
        />
      </Paper>
      {msg.err && <ToastMsg severity="error" msg={msg.errText} msgPop={() => setMsg({ err: false, errText: "", ok: false, okText: "" })} />}
      {msg.ok && <ToastMsg severity="success" msg={msg.okText} msgPop={() => setMsg({ err: false, errText: "", ok: false, okText: "" })} />}
    </Box>
  );
}
